import { useNavigateBack } from '@iheartjane/menu';
import {
  Banner,
  Box,
  Button,
  AlertIcon,
  Flex,
  Link,
  SwitchField,
  Typography,
} from '@iheartjane/reefer';
import { useFormContext } from '@iheartjane/reefer-hook-form';
import { formatCurrency } from '@iheartjane/utils';
import { useCallback, useMemo, useState } from 'react';

import {
  useKioskSpacing,
  useShouldKioskShowGold,
  useShowJanePayOnKiosk,
} from '@jane/kiosk/hooks';
import { SCREEN_NAMES, useCheckout } from '@jane/kiosk/providers';
import type { CheckoutFormFields } from '@jane/kiosk/providers';
import { getCheckoutWarningMessage, getUsername } from '@jane/kiosk/util';
import type { Cart } from '@jane/shared/types';

import {
  BirthDateField,
  CheckoutFlowButton,
  ExpirationDateField,
  FirstNameField,
  IDNumberField,
  LastInitialField,
  LastNameField,
  NotesField,
  PhoneNumberField,
  SubmitCustomerInfoButton,
} from '../CustomerInfoFields/CustomerInfoFields';
import { ButtonWrapper } from './checkout.styles';

export const CustomerInfoScreen = () => {
  const { defaultSpacing, getKioskSpacing, isIpad } = useKioskSpacing();
  const formMethods = useFormContext<CheckoutFormFields>();
  const showGold = useShouldKioskShowGold();
  const {
    agreementsError,
    agreementsLoading,
    anonymousCheckout,
    cart,
    bankAccounts,
    checkoutAgreements,
    handleLinkBankClick,
    handleLogout,
    isOrderLoading: isLoading,
    kiosk,
    kioskAgreementsEnabled,
    kioskUser,
    reopenAgreements,
    requiredConsentsValid,
    setAnonymousCheckout,
    setScreen,
    store,
    handleSubmitWithValidation,
  } = useCheckout();

  const userCallout = useMemo(() => getUsername(kioskUser), [kioskUser]);
  const goBack = useNavigateBack(`stores/${store.id}/menu`);

  const [noteShown, setNoteShown] = useState(false);

  // Agreements are accepted on their own step before this one, so reaching here
  // with them unresolved means something raced (the requirements refetched after
  // acceptance). Fail closed and reopen that step rather than letting a
  // slow/failed load silently skip consent.
  const agreementsBlockSubmit =
    kioskAgreementsEnabled &&
    (agreementsLoading ||
      agreementsError ||
      (checkoutAgreements.length > 0 && !requiredConsentsValid));

  const allowAnonymousCheckout = store.kiosk_anonymous_checkout === 'allowed';
  const strictAnonymousCheckout = Boolean(
    store.kiosk_strict_anonymous_checkout
  );
  const showJanePay = useShowJanePayOnKiosk();
  const anonymousCheckoutMessage = useMemo(
    () => getCheckoutWarningMessage({ cart: cart as Cart, store, showJanePay }),
    [cart, store, showJanePay]
  );
  const showCheckoutWarning =
    showGold && kioskUser && anonymousCheckout && anonymousCheckoutMessage;

  const showMedicalIdSection =
    store.kiosk_anonymous_checkout === 'none' &&
    (kiosk?.show_medical_id_at_checkout ||
      kiosk?.show_medical_id_expiration_at_checkout);

  const showLinkBankCallout =
    showGold &&
    !bankAccounts.length &&
    !!cart?.brand_discounts_total &&
    cart.brand_discounts_total > 0 &&
    !anonymousCheckout;

  const showContinueButton = showJanePay && !anonymousCheckout;

  const bankLinkingCopy = useMemo(() => {
    if (showJanePay) {
      return (
        <Typography>
          Don&apos;t miss +{formatCurrency(cart?.brand_discounts_total)} in cash
          back &mdash;{' '}
          <Link onClick={handleLinkBankClick}>link your bank account</Link> with
          Jane Gold.
        </Typography>
      );
    } else {
      return (
        <Typography>
          Last chance to{' '}
          <Link onClick={handleLinkBankClick}>link your bank account</Link> and
          earn +{formatCurrency(cart?.brand_discounts_total)} in cash back with
          Jane Gold.
        </Typography>
      );
    }
  }, [showJanePay]);

  const handleContinue = async () => {
    if (agreementsBlockSubmit) {
      reopenAgreements();
      return;
    }
    const isValid = await formMethods.trigger();
    if (isValid) {
      setScreen(SCREEN_NAMES.payment);
    }
  };

  const handleSubmitClick = () => {
    if (agreementsBlockSubmit) {
      reopenAgreements();
      return;
    }
    handleSubmitWithValidation();
  };

  const onAddNote = useCallback(() => {
    setNoteShown(true);
  }, []);

  return (
    <>
      <Box>
        {userCallout && (
          <Flex mb={defaultSpacing} gap={12}>
            <Typography>
              {userCallout.includes('privaterelay.appleid.com')
                ? "Didn't sign in?"
                : `Not ${userCallout}?`}
            </Typography>
            <Link onClick={handleLogout}>
              <Typography>Logout</Typography>
            </Link>
          </Flex>
        )}

        <Typography mb={defaultSpacing}>
          {strictAnonymousCheckout && anonymousCheckout
            ? `${
                kiosk?.show_checkout_message_field
                  ? 'Add a note or click'
                  : 'Click'
              } to finalize your order.`
            : 'We just need a few details from you to finalize your order.'}
        </Typography>

        {showLinkBankCallout && (
          <Banner
            icon={<AlertIcon />}
            label={bankLinkingCopy}
            variant="info"
            mb={defaultSpacing}
            data-testid="link-bank-callout"
          />
        )}

        {showCheckoutWarning && (
          <Banner
            icon={<AlertIcon />}
            label={
              <Typography color="error-dark">
                {anonymousCheckoutMessage}
              </Typography>
            }
            variant="error"
            mb={defaultSpacing}
            data-testid="checkout-warning"
          />
        )}

        {(!anonymousCheckout || !strictAnonymousCheckout) && (
          <>
            {showMedicalIdSection && (
              <Typography branded variant="h4-semibold" mb={24}>
                Contact details
              </Typography>
            )}
            <Flex gap={16} mb={getKioskSpacing(24, 16)} width="100%">
              <FirstNameField readOnly={!!kioskUser?.first_name} />
              {anonymousCheckout ? (
                <LastInitialField readOnly={!!kioskUser?.last_name} />
              ) : (
                <LastNameField readOnly={!!kioskUser?.last_name} />
              )}
            </Flex>
            {!anonymousCheckout && (
              <Flex gap={16} mb={24} width="100%">
                {!kiosk?.disable_phone_number_at_checkout && (
                  <PhoneNumberField readOnly={!!kioskUser?.phone} />
                )}
                {!kiosk?.disable_birth_date_at_checkout && (
                  <BirthDateField
                    store={store}
                    readOnly={!!kioskUser?.birth_date}
                  />
                )}
              </Flex>
            )}

            {showMedicalIdSection && (
              <>
                <Typography branded variant="h4-semibold" mb={24}>
                  Medical ID information
                </Typography>
                <Flex gap={16} mb={24} width="100%">
                  {kiosk?.show_medical_id_at_checkout && <IDNumberField />}

                  {kiosk?.show_medical_id_expiration_at_checkout && (
                    <ExpirationDateField />
                  )}
                </Flex>
              </>
            )}
          </>
        )}

        {allowAnonymousCheckout && (
          <Flex flexDirection="row" mb={getKioskSpacing(24, 16)}>
            <SwitchField
              label="Check out anonymously"
              labelHidden
              name="anonymous checkout toggle"
              checked={anonymousCheckout}
              onChange={setAnonymousCheckout}
            />
            <Typography ml={getKioskSpacing(12, 16)}>
              Check out anonymously
            </Typography>
          </Flex>
        )}

        {kiosk?.show_checkout_message_field && (
          <Flex mb={getKioskSpacing(0, 16)}>
            {!noteShown && (
              <Link
                ariaLabel="show note field"
                color="grays-mid"
                onClick={onAddNote}
                variant="underline"
              >
                <Typography>Add a note</Typography>
              </Link>
            )}
            {noteShown && <NotesField />}
          </Flex>
        )}
      </Box>

      <ButtonWrapper
        marginTop={defaultSpacing}
        gap={getKioskSpacing(24, 16)}
        isIpad={true}
      >
        <Button
          label={
            isIpad ? (
              'Cancel'
            ) : (
              <Typography as="h3" variant="h4-semibold">
                Cancel
              </Typography>
            )
          }
          onClick={() => goBack()}
          size={isIpad ? 'small' : 'large'}
          full
          variant="tertiary"
          disabled={isLoading}
        />

        {showContinueButton ? (
          <CheckoutFlowButton
            ariaLabel="continue to submit customer information and choose payment option"
            buttonLabel="Continue"
            onClick={handleContinue}
            loading={isLoading}
            size={isIpad ? 'small' : 'large'}
          />
        ) : (
          <SubmitCustomerInfoButton
            loading={isLoading}
            size={isIpad ? 'small' : 'large'}
            onClick={handleSubmitClick}
          />
        )}
      </ButtonWrapper>
    </>
  );
};
